import { Badge } from "@vds/badges";
import { TextLink } from "@vds/buttons";
import { Body, Title } from "@vds/typography";
import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import DeviceInfoTooltip from '../../../global/components/DeviceInfoTooltip/DeviceInfoTooltip';
import CustomHOCTable from "../../../global/components/Tables/CustomHOCTable";
import {
  getCompanionDataSource,
  getCompanionObjectByRefId,
  getFilteredData,
  getFilterOptions,
  isNewBadge,
} from "../../../global/utility/deviceTableUtils";
import { hasPermission } from "../../../global/utility/permissionUtils";
import {
  getFilterIcon,
  getSortingIcon,
} from "../../../global/utility/tableUtils";
import { convertCamelCaseToText, formatETA } from "../../../global/utility/Utils";
import { useAutoRefetchSelector } from "../../../hooks/useAutoRefetchSelector";
import {
  showNotificationBanner
} from "../../../store/actions";
import CompanionOnboardingModal from "../Campanion/CompanionOnboardingModal";
import './companionTab.css';
import { PAGE_IDENTIFIERS } from "../../../global/constants/tableConstants";
import { QUERY_PARAMS, ROUTES } from "../../../global/constants/routesConstants";
import { COMPANION_OFFLINE_NOTIFICATION } from "../../../constants/DeviceConstants";
import { setDetailNavSource } from "../../../hooks/usePaginationReset";
import { isCcdHasInHouseVersion } from "../../../global/utility/devicesUtils";

export const CompanionTab = (props) => {
  const CompanionDeviceTable = CustomHOCTable();
  const [loading, setLoading] = useState(true);
  const dispatch = useDispatch();
  // Replace useSelector calls with useAutoRefetchSelector using simplified syntax
  const { data: companionList, isLoading: companionLoading, refetch: refetchCompanions } = useAutoRefetchSelector(
    (state) => state.deviceReducer.companionDeviceList
  );
  
  const { data: sitesTagList, isLoading: sitesLoading } = useAutoRefetchSelector(
    (state) => state.sitesReducer.siteTagList
  );

  const { data: ccdUpdateList, isLoading: ccdUpdateLoading } = useAutoRefetchSelector(
    (state) => state.deviceReducer.ccdUpdateList
  );
  
  //const companionList = props.companionData.companionDeviceList;
  const [companionDeviceDataSource, setCompanionDeviceDataSource] =
    useState(null);
  const [selectedRowId, setSelectedRowId] = useState([]);
  const [showCompanionModal, setShowCompanionModal] = useState(false);
  const updatedDate = sessionStorage.getItem("updatedDate");
  const sites = sitesTagList?.sites;

  const navigate = useNavigate();

  const handleOpenModal = (id) => {
    if (!hasPermission("Companions", "Edit")) {
      showPermissionsBanner();
    } else {
      setSelectedRowId(id);
      setShowCompanionModal(true);
    }
  };

  const columns = [
    {
      title: (
        <Title size="small" bold primitive="h2" viewport="desktop">
          Name
        </Title>
      ),
      dataIndex: "Name",
      key: "Name",
      sortDirections: ["descend", "ascend"],
      sorter: (a, b) => {
        const nameA = a.Name || "";
        const nameB = b.Name || "";
        return nameA.localeCompare(nameB);
      },
      sortIcon: ({ sortOrder }) => getSortingIcon(sortOrder),
      render: (_, record) => {
        const companionDeviceData = getCompanionObjectByRefId(
          record.Id,
          companionList
        );
        const state = {
          data: { ...companionDeviceData[0] },
          title: `More info for ${record.Name}`,
          fields: companionDeviceData[0]?.fields || {},
        };
        const showBadge = isNewBadge(record.LastUpdatedOn);

        return (
          <div style={{ display: "flex" }}>
            <span style={{ marginRight: "5px" }}>
              {showBadge && <Badge fillColor="black">New</Badge>}
            </span>

            <div className="name-with-info">
              {record?.TagIds?.length > 0 ? (
                <>
                  {record?.Status?.toLowerCase() === "offline" ? (
                    <TextLink
                      type="standAlone"
                      data-testid="devices_companionTabCompanionLink"
                      onClick={() => showOfflineNotification()}
                      size="large"
                      className="link"
                      style={{
                        cursor: "pointer",
                      }}
                    >
                      <Body size="large" viewport="desktop" primitive="span">
                        {record.Name}
                      </Body>
                    </TextLink>
                  ) : (
                    <TextLink
                      type="standAlone"
                      data-testid="devices_companionTabCDpageTextLink"
                      onClick={() => {
                        setDetailNavSource(PAGE_IDENTIFIERS.COMPANION_TAB);
                        navigate(`${ROUTES.COMPANION_DEVICE_INFO_PAGE}?${QUERY_PARAMS.CCD_ID}=${companionDeviceData[0]?.id}`, { state });
                      }}
                      size="large"
                      className="link"
                      style={{
                        cursor: "pointer",
                      }}
                    >
                      <Body size="large" viewport="desktop" primitive="span">
                        {record.Name}
                      </Body>
                    </TextLink>
                  )}
                </>
              ) : (
                <TextLink
                  type="standAlone"
                  data-testid="devices_companionTabCompanionTextLinkOpenModal"
                  onClick={() => handleOpenModal(record.Id)}
                  size="large"
                  className="link"
                  style={{
                    cursor: "pointer",
                  }}
                >
                  <Body size="large" viewport="desktop" primitive="span">
                    {record.Name}
                  </Body>
                </TextLink>
              )}
              
              <DeviceInfoTooltip
                serial={companionDeviceData?.[0]?.serial}
                make={companionDeviceData?.[0]?.manufacturer}
                model={companionDeviceData?.[0]?.productmodel}
                companionVersion={companionDeviceData?.[0] && isCcdHasInHouseVersion(companionDeviceData[0]) ? "V2" : "V1"}
              />
            </div>
          </div>
        );
      },
    },
    {
      title: (
        <Title size="small" bold primitive="h2" viewport="desktop">
          Status
        </Title>
      ),
      dataIndex: "Status",
      key: "Status",
      sortDirections: ["ascend", "descend"],
      sorter: (a, b) => a.Status.length - b.Status.length,
      sortIcon: ({ sortOrder }) => getSortingIcon(sortOrder),
      isFilter: true,
      filter: getFilterOptions(companionDeviceDataSource, "Status"),
      onFilter: (value, record) => {
        return record.Status === value;
      },
      filterIcon: (filtered) => getFilterIcon(filtered),
      render: (text, record) => (
        <span style={{ marginLeft: "10px" }}>
          {record?.TagIds?.length > 0 ? (
            <>
              {record?.Status?.toLowerCase() === "offline" ? (
                <Badge fillColor={"orange"}>Offline</Badge>
              ) : record?.Status?.toLowerCase() === "inprogress" ? (
                <div style={{ display: "flex", flexDirection: "column", justifyContent: "flex-start", alignItems: "flex-start" }}>
                  <div style={{ display: "inline-block", width: "max-content" }}>
                    <Badge fillColor={"blue"}>Updating</Badge>
                  </div>
                  {record?.ETA && <div>ETA: {formatETA(record?.ETA)}</div>}
                </div>
              ) : (
                <span>{convertCamelCaseToText(record?.Status)}</span>
              )}
            </>
          ) : (
            <Badge fillColor={"yellow"}>Pending</Badge>
          )}
        </span>
      ),
    },
    {
      title: (
        <Title size="small" bold primitive="h2" viewport="desktop">
          Location
        </Title>
      ),
      dataIndex: "Location",
      key: "Location",
      sortDirections: ["ascend", "descend"],
      sorter: (a, b) => a.Location.length - b.Location.length,
      sortIcon: ({ sortOrder }) => getSortingIcon(sortOrder),
      isFilter: true,
      filter: getFilterOptions(companionDeviceDataSource, "Location"),
      onFilter: (value, record) => getFilteredData(value, record, "Location"),
      filterIcon: (filtered) => getFilterIcon(filtered),
    },
    {
      title: (
        <Title size="small" bold primitive="h2" viewport="desktop">
          Software
        </Title>
      ),
      dataIndex: "Firmware",
      key: "Firmware",
      sortDirections: ["ascend", "descend"],
      sorter: (a, b) => a.Firmware.length - b.Firmware.length,
      sortIcon: ({ sortOrder }) => getSortingIcon(sortOrder),
      isFilter: true,
      filter: getFilterOptions(companionDeviceDataSource, "Firmware"),
      onFilter: (value, record) => getFilteredData(value, record, "Firmware"),
      filterIcon: (filtered) => getFilterIcon(filtered),
    },
  ];

  // Set loading based on auto-refetch selector loading states
  useEffect(() => {
    const isAnyLoading = companionLoading || sitesLoading || ccdUpdateLoading;
    setLoading(isAnyLoading);
  }, [companionLoading, sitesLoading, ccdUpdateLoading]);

  const getCompanionDeviceData = () => {
    let companionDeviceData = getCompanionDataSource(companionList, sites, ccdUpdateList);
    setCompanionDeviceDataSource(companionDeviceData);
  };

  useEffect(() => {
    getCompanionDeviceData();
  }, [companionList, ccdUpdateList, sites]);

  const customNoDataText = {
    emptyText: (
      <div style={{ color: "black" }}>
        <center>
          <br></br>
          {!companionDeviceDataSource?.length > 0 && (
            <>
              <div style={{ marginBottom: "8px" }}>
                <Title size="large" primitive="h3">
                  No companion devices
                </Title>
              </div>
            </>
          )}
          {companionDeviceDataSource?.length > 0 && (
            <>
              <div style={{ marginBottom: "8px" }}>
                <Title size="large" primitive="h3">
                  No companion devices found
                </Title>
              </div>
              <div style={{ marginBottom: "8px" }}>
                <Body size="large" viewport="desktop">
                  Try adjusting your search or filters.
                </Body>
              </div>
            </>
          )}
        </center>
      </div>
    ),
  };
  const showPermissionsBanner = () => {
    const notificationInfo = {
      type: "No Permission",
      priority: "critical",
      showNotification: true,
    };
    dispatch(showNotificationBanner(notificationInfo));
  };

  const showOfflineNotification = () => {
    dispatch(showNotificationBanner(COMPANION_OFFLINE_NOTIFICATION));
  };

  return (
    <div>
      <div
        style={{
          textAlign: "left",
          padding: "20px",
          paddingLeft: "0px",
        }}
      >
        {companionDeviceDataSource &&
          Array.isArray(companionDeviceDataSource) &&
          companionDeviceDataSource?.length > 0 && (
            <Body size="large" bold={true}>
              To add pending companion, click on the device name.
            </Body>
          )}
      </div>

      <CompanionDeviceTable
        showSearch={true}
        columns={columns}
        dataList={companionDeviceDataSource}
        rowKey="id"
        addDetailsButton={true}
        addDetailsButtonStyle={{ position: "relative", right: "50px" }}
        locale={customNoDataText}
        loading={loading}
        searchTextPlaceholder={"Search companion devices"}
        updatedDate={updatedDate}
        pageIdentifier={PAGE_IDENTIFIERS.COMPANION_TAB}
        useReduxPersistence={true}
      />
      <CompanionOnboardingModal
        title="Companion device details"
        showModal={showCompanionModal}
        setShowModal={setShowCompanionModal}
        selectedRowId={selectedRowId}
      />
    </div>
  );
};

export default CompanionTab;
